import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { loadRaidControlUnread } from '../../raid-control/api/raidControlRealtimeClient.js';
import { raidControlTimelineMessageVisible } from '../../raid-control/domain/raidControlTimeline.js';


function seenKey(unionId) { return `doroparty:notice-seen:${String(unionId || '')}`; }
function readSeen(unionId) { if (!unionId || typeof window === 'undefined') return ''; try { return window.localStorage.getItem(seenKey(unionId)) || ''; } catch { return ''; } }
function writeSeen(unionId, value) { if (!unionId || !value || typeof window === 'undefined') return; try { window.localStorage.setItem(seenKey(unionId), value); } catch { /* storage unavailable */ } }
// ISO 8601 UTC 문자열이라 사전순이 곧 시간순이지만, 형식이 다를 수 있으니 시각으로 견준다.
function readPointIsNewer(candidate, current) {
  if (!candidate) return false;
  if (!current) return true;
  const next = Date.parse(candidate);
  const now = Date.parse(current);
  if (!Number.isFinite(next)) return false;
  if (!Number.isFinite(now)) return true;
  return next > now;
}

function isMissingTable(error, table) { const raw = String(error?.message || ''); return error?.code === '42P01' || new RegExp(`${table}.*does not exist|relation .*${table}`, 'i').test(raw); }

// 배지는 '내가 아닌 사람의 새 글이 있나' 하나만 알면 된다. 전에는 그걸 보려고 최근 160개와
// 지시·실전 목록을 통째로 받았고, 그 조회가 스냅샷을 거치느라 소켓까지 열었다 닫았다.
//
// 이제 방에 그 한 가지만 묻는다. 지시 메시지는 지시 상태에 따라 안 보일 수 있는데(완료된
// 지시) 그 상태는 Supabase 소유라 방이 모른다. 그래서 읽은 지점 뒤의 지시 메시지가 있을
// 때만 그 지시들의 상태를 따로 묻는다 - 대부분은 왕복 한 번으로 끝난다.
async function loadLatestVisibleRaidControlAt(unionId, userId) {
  const seasonResult = await supabase
    .from('raid_seasons')
    .select('id,season_no')
    .eq('union_id', unionId)
    .eq('status', 'active')
    .order('season_no', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (seasonResult.error) return { data: null, error: seasonResult.error, seasonId: undefined, lastReadAt: '' };
  if (!seasonResult.data?.id) return { data: null, error: null, seasonId: '', lastReadAt: '' };
  const seasonId = String(seasonResult.data.id);
  const seasonNo = Number(seasonResult.data.season_no || 0);

  try {
    const unread = await loadRaidControlUnread({ seasonNo });
    let createdAt = String(unread.latestAt || '');
    const orderMessages = Array.isArray(unread.orderMessages) ? unread.orderMessages : [];
    if (orderMessages.length) {
      const orderIds = [...new Set(orderMessages.map((message) => String(message.orderId || '')).filter(Boolean))];
      const orderResult = await supabase.from('raid_control_orders').select('id,status').in('id', orderIds);
      const ordersById = new Map((orderResult.data || []).map((row) => [String(row.id), { id: String(row.id), status: String(row.status || '') }]));
      for (const message of orderMessages) {
        if (!raidControlTimelineMessageVisible({ type: 'attack_order', orderId: message.orderId }, ordersById)) continue;
        const at = String(message.createdAt || '');
        if (!at) continue;
        if (!createdAt || Date.parse(at) > Date.parse(createdAt)) createdAt = at;
      }
    }
    return {
      data: createdAt ? { created_at: createdAt } : null,
      error: null,
      seasonId,
      lastReadAt: String(unread.lastReadAt || ''),
    };
  } catch (error) {
    return { data: null, error, seasonId, lastReadAt: '' };
  }
}

export function useNavigationBadges({ page = '', unionId = '', userId = '', canManagePlatform = false } = {}) {
  const [latestNoticeAt, setLatestNoticeAt] = useState('');
  const [seenNoticeAt, setSeenNoticeAt] = useState(() => readSeen(unionId));
  const [latestControlAt, setLatestControlAt] = useState('');
  const [readControlAt, setReadControlAt] = useState('');
  const [activeControlSeasonId, setActiveControlSeasonId] = useState('');
  const [supportCount, setSupportCount] = useState(0);
  const [platformCount, setPlatformCount] = useState(0);
  const controlPageActive = page === 'control';
  const refreshRunRef = useRef(0);

  const refresh = useCallback(async () => {
    const runId = ++refreshRunRef.current;
    if (!hasSupabaseConfig || !supabase) { setLatestNoticeAt(''); setLatestControlAt(''); setReadControlAt(''); setActiveControlSeasonId(''); setSupportCount(0); setPlatformCount(0); return; }
    const noticePromise = unionId ? supabase.from('union_notices').select('created_at').eq('union_id', unionId).order('created_at', { ascending: false }).limit(1).maybeSingle() : Promise.resolve({ data: null, error: null });
    const controlPromise = unionId && userId && !controlPageActive ? loadLatestVisibleRaidControlAt(unionId, userId) : Promise.resolve({ data: null, error: null, seasonId: '', lastReadAt: '' });
    const supportPromise = canManagePlatform ? supabase.from('site_support_requests').select('id', { count: 'exact', head: true }).in('status', ['open', 'in_progress']) : Promise.resolve({ count: 0, error: null });
    const platformPromise = canManagePlatform ? supabase.from('unions').select('id', { count: 'exact', head: true }).eq('registration_status', 'pending') : Promise.resolve({ count: 0, error: null });
    const [noticeResult, controlResult, supportResult, platformResult] = await Promise.all([noticePromise, controlPromise, supportPromise, platformPromise]);
    if (refreshRunRef.current !== runId) return;
    if (!noticeResult.error) setLatestNoticeAt(String(noticeResult.data?.created_at || ''));
    if (!controlPageActive && controlResult.seasonId !== undefined) setActiveControlSeasonId(String(controlResult.seasonId || ''));
    if (!controlPageActive && !controlResult.error) {
      setLatestControlAt(String(controlResult.data?.created_at || ''));
      // 서버 값이 로컬 바닥값보다 뒤면 그대로 둔다. 아직 안 올라간 읽음을 되돌리지 않는다.
      setReadControlAt((current) => {
        const serverAt = String(controlResult.lastReadAt || '');
        return readPointIsNewer(serverAt, current) ? serverAt : current;
      });
    }
    if (!supportResult.error || isMissingTable(supportResult.error, 'site_support_requests')) setSupportCount(Number(supportResult.count || 0));
    if (!platformResult.error) setPlatformCount(Number(platformResult.count || 0));
  }, [canManagePlatform, controlPageActive, unionId, userId]);

  useEffect(() => {
    setSeenNoticeAt(readSeen(unionId)); refresh();
    const handleChange = () => refresh();
    // 사건이 실어 온 읽은 지점을 바닥값으로 깔아 둔다. 서버가 아직 모르는 지점이라도 배지는
    // 더 뒤로 가지 않는다.
    const handleRaidControlRead = (event) => {
      const at = String(event?.detail?.at || '');
      if (at) setReadControlAt((current) => (readPointIsNewer(at, current) ? at : current));
      if (!controlPageActive) refresh();
    };
    let lastResumeRefreshAt = 0;
    const handleResume = () => {
      if (controlPageActive || document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastResumeRefreshAt < 1_000) return;
      lastResumeRefreshAt = now;
      refresh();
    };
    window.addEventListener('doroparty:notice-changed', handleChange);
    window.addEventListener('doroparty:site-support-changed', handleChange);
    window.addEventListener('dororaid:union-registration-changed', handleChange);
    window.addEventListener('doroparty:raid-control-read', handleRaidControlRead);
    window.addEventListener('pageshow', handleResume);
    window.addEventListener('focus', handleResume);
    document.addEventListener('visibilitychange', handleResume);
    return () => {
      refreshRunRef.current += 1;
      window.removeEventListener('doroparty:notice-changed', handleChange);
      window.removeEventListener('doroparty:site-support-changed', handleChange);
      window.removeEventListener('dororaid:union-registration-changed', handleChange);
      window.removeEventListener('doroparty:raid-control-read', handleRaidControlRead);
      window.removeEventListener('pageshow', handleResume);
      window.removeEventListener('focus', handleResume);
      document.removeEventListener('visibilitychange', handleResume);
    };
  }, [controlPageActive, refresh, unionId]);

  // 배지 하나 때문에 관제 '밖'의 모든 페이지에서 관제 방 소켓을 붙들고 있었다. 홈·모의전·
  // 테트리스·정산 어디에 있든 로그인해 있기만 하면 붙었다.
  //
  // 안 읽음 여부는 서버만 알지만 실시간으로 알 이유는 없다. 다른 화면을 보는 동안 배지가
  // 몇 분 늦는 것은 사소하고, 멘션은 이미 푸시로 간다. 위 효과의 refresh 가 들어올 때,
  // 창으로 돌아올 때, 탭을 다시 볼 때 가벼운 조회(왕복 1회)로 확인한다.


  useEffect(() => { if (page === 'notice' && latestNoticeAt) { writeSeen(unionId, latestNoticeAt); setSeenNoticeAt(latestNoticeAt); } }, [latestNoticeAt, page, unionId]);
  const noticeUnread = Boolean(latestNoticeAt && (!seenNoticeAt || new Date(latestNoticeAt).getTime() > new Date(seenNoticeAt).getTime()));
  const raidControlUnread = !controlPageActive && Boolean(userId && latestControlAt && (!readControlAt || new Date(latestControlAt).getTime() > new Date(readControlAt).getTime()));
  const managementCount = useMemo(() => supportCount + platformCount, [platformCount, supportCount]);
  return { noticeUnread, raidControlUnread, supportCount, platformCount, managementCount, refresh };
}
